import React from 'react';
import {
  CHAIN_LABELS, CHAIN_COLORS, formatNum, formatMC, formatTime, getNativeSymbol,
  buildChartUrl, buildDexScreenerUrl, buildAxiomUrl, buildGmgnUrl, buildFomoUrl, getStrategyMeta,
} from '../../utils/masterChefUtils';
import ManualCloseButtons from './ManualCloseButtons';

export default function ChartModal({
  position, onClose, onSell, exitConfig, onPositionUpdate,
}) {
  if (!position) return null;
  const p = position;
  const pnlPct = p.status === 'active' ? p.unrealizedPnlPercent : p.totalPnlPercent;
  const multiple = p.currentMultiple || 1;

  // Token supply implied by entry MC ÷ entry price — lets us express every
  // price level as a market cap (far easier to read than 8-decimal prices).
  const supply = (p.entryMarketCap > 0 && p.avgEntryPrice > 0)
    ? p.entryMarketCap / p.avgEntryPrice
    : 0;
  const mcFor = (price, knownMc = 0) => {
    if (knownMc > 0) return knownMc;
    if (supply > 0 && price > 0) return price * supply;
    return 0;
  };

  // Row: market cap as the headline value, raw price as small subtext.
  const McRow = ({ label, price, knownMc = 0, color = 'text-gray-200' }) => {
    const mc = mcFor(price, knownMc);
    return (
      <div className="flex justify-between text-xs">
        <span className="text-gray-500">{label}</span>
        <span className="text-right leading-tight">
          <span className={`${color} font-mono block`}>
            {mc > 0 ? formatMC(mc) : `$${formatNum(price, 8)}`}
          </span>
          {mc > 0 && price > 0 && (
            <span className="text-gray-500 font-mono text-[10px]">${formatNum(price, 8)}</span>
          )}
        </span>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-gray-700" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-white">{p.tokenSymbol}</span>
                <span className={`px-1.5 py-0.5 text-xs font-medium rounded bg-${CHAIN_COLORS[p.chain] || 'gray'}-900 text-${CHAIN_COLORS[p.chain] || 'gray'}-300 border border-${CHAIN_COLORS[p.chain] || 'gray'}-700`}>
                  {CHAIN_LABELS[p.chain] || p.chain}
                </span>
                <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${getStrategyMeta(p).chart}`}>
                  {getStrategyMeta(p).full}
                </span>
                <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                  p.status === 'active' ? 'bg-green-900 text-green-300' :
                  p.status === 'closed' ? 'bg-gray-700 text-gray-300' :
                  'bg-red-900 text-red-300'
                }`}>
                  {p.status}
                </span>
                {p.executionMode === 'live' && (
                  <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-green-900 text-green-300 border border-green-700">LIVE</span>
                )}
              </div>
              <p className="text-xs text-gray-400 font-mono mt-0.5">{p.tokenAddress}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href={buildDexScreenerUrl(p.chain, p.tokenAddress)} target="_blank" rel="noopener noreferrer"
              className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white border border-gray-600 transition-colors">
              📊 Chart
            </a>
            {buildFomoUrl(p.chain, p.tokenAddress) && (
              <a href={buildFomoUrl(p.chain, p.tokenAddress)} target="_blank" rel="noopener noreferrer"
                className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white border border-gray-600 transition-colors">
                Fomo ↗
              </a>
            )}
            <a href={buildAxiomUrl(p.chain, p.tokenAddress)} target="_blank" rel="noopener noreferrer"
              className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white border border-gray-600 transition-colors">
              Axiom ↗
            </a>
            <a href={buildGmgnUrl(p.chain, p.tokenAddress)} target="_blank" rel="noopener noreferrer"
              className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white border border-gray-600 transition-colors">
              GMGN ↗
            </a>
            <button onClick={onClose} className="ml-2 text-gray-400 hover:text-white text-xl leading-none">&times;</button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row">
          <div className="flex-1 min-h-[400px] lg:min-h-[500px] bg-black">
            <iframe
              src={buildChartUrl(p.chain, p.tokenAddress)}
              title={`${p.tokenSymbol} chart`}
              className="w-full h-full min-h-[400px] lg:min-h-[500px] border-0"
              sandbox="allow-scripts allow-same-origin allow-popups"
            />
          </div>

          <div className="w-full lg:w-72 border-t lg:border-t-0 lg:border-l border-gray-700 p-4 overflow-y-auto max-h-[300px] lg:max-h-[500px]">
            <div className="mb-4">
              <p className="text-xs text-gray-400 uppercase mb-1">Current Multiple</p>
              <p className={`text-3xl font-bold ${multiple >= 2 ? 'text-green-400' : multiple < 1 ? 'text-red-400' : 'text-white'}`}>
                {multiple.toFixed(2)}x
              </p>
              <p className={`text-sm font-medium ${pnlPct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {pnlPct >= 0 ? '+' : ''}{pnlPct?.toFixed(1)}% P&L
              </p>
            </div>

            <div className="space-y-2 mb-4">
              <McRow label="Entry MC" price={p.avgEntryPrice} knownMc={p.entryMarketCap} />
              <McRow label="Current MC" price={p.currentPrice} knownMc={p.currentMarketCap} />
              {p.highestPriceSinceEntry > 0 && (
                <McRow label="ATH MC Since Entry" price={p.highestPriceSinceEntry} />
              )}
              {p.stopLossPrice > 0 && (
                <McRow label="Stop Loss MC" price={p.stopLossPrice} color="text-red-400" />
              )}
              {p.trailingStopPrice > 0 && (
                <McRow label="Trailing Stop MC" price={p.trailingStopPrice} color="text-orange-400" />
              )}
            </div>

            <div className="space-y-2 mb-4 border-t border-gray-700 pt-3">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Invested</span>
                <span className="text-gray-200 font-mono">{formatNum(p.totalInvested, 4)} {getNativeSymbol(p.chain)}</span>
              </div>
              {p.totalRealized > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Realized</span>
                  <span className="text-green-400 font-mono">{formatNum(p.totalRealized, 4)}</span>
                </div>
              )}
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Next Exit Tier</span>
                <span className="text-gray-200">{p.status === 'active' ? `T${p.nextExitTier}` : '-'}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Opened</span>
                <span className="text-gray-400">{formatTime(p.openedAt)}</span>
              </div>
              {p.closedAt && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Closed</span>
                  <span className="text-gray-400">{formatTime(p.closedAt)}</span>
                </div>
              )}
            </div>

            {p.entries && p.entries.length > 0 && (
              <div className="mb-3 border-t border-gray-700 pt-3">
                <p className="text-xs text-gray-400 uppercase mb-2">Entries ({p.entries.length})</p>
                {p.entries.map((e, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-gray-800 last:border-0">
                    <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                    <span className="text-gray-400 font-mono">${formatNum(e.price, 8)}</span>
                    <span className="text-gray-600 ml-auto">{formatTime(e.timestamp)}</span>
                  </div>
                ))}
              </div>
            )}

            {p.exits && p.exits.length > 0 && (
              <div className="border-t border-gray-700 pt-3">
                <p className="text-xs text-gray-400 uppercase mb-2">Exits ({p.exits.length})</p>
                {p.exits.map((e, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-gray-800 last:border-0">
                    <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                    <span className="text-gray-400 font-mono">T{e.tier} @ {e.triggerMultiple}x</span>
                    <span className="text-green-400 font-mono ml-auto">{formatNum(e.amountReceived, 4)}</span>
                  </div>
                ))}
              </div>
            )}

            <ManualCloseButtons
              position={p}
              exitConfig={exitConfig}
              onSell={onSell}
              onPositionUpdate={onPositionUpdate}
              onClose={onClose}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
